import React, { forwardRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Slide,
  useMediaQuery,
  useTheme,
  Box
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

// Slide transition for mobile
const SlideTransition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/**
 * BaseModal - A unified, responsive modal component
 * 
 * Features:
 * - Mobile-first responsive design
 * - Consistent styling across all modals
 * - Customizable sizes and behaviors
 * - Accessibility compliant
 * - Smooth animations
 */
const BaseModal = ({
  // Core props
  open,
  onClose,
  title,
  children,
  
  // Action buttons
  actions,
  
  // Customization
  maxWidth = 'sm',
  fullWidth = true,
  size = 'medium', // 'small', 'medium', 'large', 'fullscreen'
  
  // Behavior
  disableBackdropClick = false,
  disableEscapeKeyDown = false,
  hideCloseButton = false,
  
  // Mobile specific
  mobileFullScreen = false,
  
  // Styling
  contentSx = {},
  titleSx = {},
  actionsSx = {},
  
  // Advanced
  TransitionComponent = SlideTransition,
  transitionDuration = 300,
  
  // Accessibility
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
  
  ...otherProps
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  // Determine if we should use mobile fullscreen
  const shouldUseFullScreen = isMobile && (mobileFullScreen || size === 'fullscreen');
  
  // Calculate responsive maxWidth
  const getResponsiveMaxWidth = () => {
    if (shouldUseFullScreen) return false;
    
    switch (size) {
      case 'small': return isMobile ? 'xs' : 'xs';
      case 'medium': return isMobile ? 'sm' : maxWidth;
      case 'large': return isMobile ? 'md' : 'md';
      case 'fullscreen': return false;
      default: return maxWidth;
    }
  };
  
  // Handle close with backdrop check
  const handleClose = (event, reason) => {
    if (disableBackdropClick && reason === 'backdropClick') {
      return;
    }
    if (disableEscapeKeyDown && reason === 'escapeKeyDown') {
      return;
    }
    onClose?.(event, reason);
  };
  
  // Calculate responsive padding and margins
  const getContentPadding = () => {
    if (isMobile) return { px: 2, py: 1.5 };
    return { px: 3, py: 2 };
  };
  
  const getActionsPadding = () => {
    if (isMobile) return { px: 2, py: 1.5, gap: 1 };
    return { px: 3, py: 2, gap: 1.5 };
  };
  
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={getResponsiveMaxWidth()}
      fullWidth={fullWidth}
      fullScreen={shouldUseFullScreen}
      TransitionComponent={shouldUseFullScreen ? SlideTransition : undefined}
      transitionDuration={transitionDuration}
      aria-labelledby={ariaLabelledBy || 'modal-title'}
      aria-describedby={ariaDescribedBy || 'modal-description'}
      PaperProps={{
        sx: {
          // Responsive border radius
          borderRadius: shouldUseFullScreen ? 0 : isMobile ? 2 : 3,
          
          // Responsive margins
          margin: shouldUseFullScreen ? 0 : isMobile ? 1 : 2,
          
          // Max height constraints
          maxHeight: shouldUseFullScreen ? '100vh' : isMobile ? '90vh' : '85vh',
          
          // Ensure proper scrolling
          display: 'flex',
          flexDirection: 'column',
          
          // Custom styles
          ...otherProps.PaperProps?.sx
        }
      }}
      {...otherProps}
    >
      {/* Title Section */}
      {title && (
        <DialogTitle
          id={ariaLabelledBy || 'modal-title'}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 1,
            // Responsive padding
            px: isMobile ? 2 : 3,
            pt: isMobile ? 2 : 3,
            // Prevent shrinking
            flexShrink: 0,
            ...titleSx
          }}
        >
          <Typography 
            variant={isMobile ? 'h6' : 'h5'} 
            component="h2"
            sx={{ 
              fontWeight: 600,
              pr: hideCloseButton ? 0 : 2 
            }}
          >
            {title}
          </Typography>
          
          {!hideCloseButton && (
            <IconButton
              aria-label="close"
              onClick={(e) => {
                e.stopPropagation();
                handleClose(e, 'closeButtonClick');
              }}
              size={isMobile ? 'small' : 'medium'}
              sx={{
                color: theme.palette.grey[500],
                '&:hover': {
                  backgroundColor: theme.palette.grey[100],
                }
              }}
            >
              <CloseIcon fontSize={isMobile ? 'small' : 'medium'} />
            </IconButton>
          )}
        </DialogTitle>
      )}
      
      {/* Content Section */}
      <DialogContent
        id={ariaDescribedBy || 'modal-description'}
        sx={{
          // Responsive padding
          ...getContentPadding(),
          
          // Enable scrolling when content overflows
          overflowY: 'auto',
          overflowX: 'hidden',
          
          // Flex grow to take available space
          flex: 1,
          
          // Ensure minimum height on mobile
          minHeight: isMobile ? 'auto' : 'min-content',
          
          // Custom styles
          ...contentSx
        }}
      >
        {children}
      </DialogContent>
      
      {/* Actions Section */}
      {actions && (
        <DialogActions
          sx={{
            // Responsive padding and gap
            ...getActionsPadding(),
            
            // Prevent shrinking
            flexShrink: 0,
            
            // Mobile-specific adjustments
            flexDirection: isMobile ? 'column-reverse' : 'row',
            
            // Ensure buttons take full width on mobile
            '& > *': isMobile ? {
              width: '100%',
              margin: '0 !important'
            } : {},
            
            // Ensure buttons are clickable
            '& button': {
              pointerEvents: 'auto',
              zIndex: 1
            },
            
            // Custom styles
            ...actionsSx
          }}
        >
          {actions}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default BaseModal;
